import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Interface for entity types - compatible with both MockEntity and DataverseEntityInfo
interface SearchableEntity {
  LogicalName?: string;
  SchemaName?: string;
  DisplayName?: string;
}

interface EntitySearchSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  entities: SearchableEntity[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
}

export const EntitySearchSelect: React.FC<EntitySearchSelectProps> = ({
  value,
  onValueChange,
  entities,
  placeholder = "Select entity...",
  disabled = false,
  isLoading = false,
  className
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const selectedEntity = entities.find(entity => entity.LogicalName === value);

  // Filter entities based on search query - only include entities with LogicalName
  const filteredEntities = React.useMemo(() => {
    // Filter out entities without LogicalName first
    const validEntities = entities.filter(entity => entity.LogicalName);

    if (!searchQuery.trim()) return validEntities;

    const query = searchQuery.toLowerCase();
    return validEntities.filter(entity =>
      (entity.SchemaName || '').toLowerCase().includes(query) ||
      (entity.LogicalName || '').toLowerCase().includes(query) ||
      (entity.DisplayName || '').toLowerCase().includes(query)
    );
  }, [entities, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            "w-full justify-between bg-background px-3 font-normal outline-offset-0 hover:bg-background focus-visible:border-ring focus-visible:outline-[3px] focus-visible:outline-ring/20",
            className
          )}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {selectedEntity
              ? (selectedEntity.SchemaName || selectedEntity.LogicalName)
              : placeholder
            }
          </span>
          <ChevronDown
            size={16}
            strokeWidth={2}
            className="shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-[var(--radix-popper-anchor-width)] border-input p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type to search entities..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {filteredEntities.length === 0 ? (
              <CommandEmpty>No entities found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredEntities.map((entity) => (
                  <CommandItem
                    key={entity.LogicalName}
                    value={entity.LogicalName!}
                    onSelect={(currentValue) => {
                      onValueChange(currentValue === value ? "" : currentValue);
                      setOpen(false);
                      setSearchQuery("");
                    }}
                    className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                  >
                    <div className="flex flex-col w-full">
                      <span className="font-medium text-foreground">{entity.SchemaName || entity.LogicalName}</span>
                      {entity.SchemaName && entity.LogicalName !== entity.SchemaName && (
                        <span className="text-xs text-muted-foreground/80">{entity.LogicalName}</span>
                      )}
                    </div>
                    {value === entity.LogicalName && (
                      <Check size={16} strokeWidth={2} className="ml-auto text-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};